import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "@/lib/brand";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Documents", href: "/documents" },
  { label: "Fees", href: "/schedule" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ theme = "dark" }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = theme === "light";
  const solid = scrolled || light;

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else navigate("/" + href);
    }
  };

  const textColor = solid && light ? "text-[#0A0D0B]" : "text-[#F5F3EC]";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        solid
          ? light
            ? "bg-[#FDFCF8]/85 backdrop-blur-xl border-b border-[#E5E1D8]"
            : "bg-[#0A0D0B]/70 backdrop-blur-xl border-b border-[#233027]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/scales.png" alt="" aria-hidden="true" className="h-7 w-auto md:h-8 opacity-95 group-hover:opacity-100 transition-opacity" />
          <span
            className="font-display text-2xl md:text-[26px] tracking-tight leading-none"
            style={{ color: light && solid ? "#0A0D0B" : "#F5F3EC" }}
          >
            Grafton<span className="text-[#B89874]">.</span>
          </span>
          <span className="hidden sm:block text-[10px] uppercase tracking-[0.28em] text-[#B89874] font-medium">
            Group
          </span>
        </Link>

        <nav className={`hidden lg:flex items-center gap-9 ${textColor}`}>
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-[13px] uppercase tracking-[0.14em] font-medium opacity-80 hover:opacity-100 hover:text-[#B89874] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#contact")}
            data-testid="nav-call"
            className="hidden md:flex items-center gap-2 text-[13px] font-semibold px-5 py-2.5 border border-[#B89874] text-[#B89874] hover:bg-[#B89874] hover:text-[#0A0D0B] transition-colors duration-300"
          >
            <Phone size={14} /> Contact Us
          </button>
          <button
            className="lg:hidden p-2"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            style={{ color: light && solid ? "#0A0D0B" : "#F5F3EC" }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A0D0B] border-t border-[#233027] px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-left text-[#F5F3EC] text-lg font-display"
            >
              {l.label}
            </button>
          ))}
          <a
            href={BRAND.phoneHref}
            className="mt-2 text-center py-3 border border-[#B89874] text-[#B89874] uppercase tracking-widest text-sm"
          >
            Call {BRAND.phone}
          </a>
        </div>
      )}
    </header>
  );
}
